import { Flex } from '@chakra-ui/react';
import { FormFieldArticleType } from '@publishing-engine/domain/types/article';
import React, { useState } from 'react';
import ArticleTypeUpdateModal from './ArticleTypeUpdateModal';
import { FieldHeader } from '../common/FieldHeader';
import {
  IAvailableWorkflowActions,
  IChangeArticleType
} from '@publishing-engine/domain/models';
import { useSubmissionData } from 'src/contexts/SubmissionDataProvider';
import { EntryPoint } from 'src/utils/types';
import { useSubmissionFields } from 'src/hooks/useSubmissionFields';
import { SelectEmptyState } from '../Select/SelectEmptyState';
import { TextDisplay } from '../Text/TextDisplay';
import { TextDiffModal } from '../Text/TextDiffModal';
import { useFlag } from '@unleash/proxy-client-react';
import { FEATURE_FLAGS } from '../../../../constants';

type IProps = {
  formFieldDefinition: FormFieldArticleType;
  value: string;
  previousValue?: string | null;
  availableActions: IAvailableWorkflowActions;
  onChange?: (value: IChangeArticleType) => void;
  entryPoint: EntryPoint;
  isUpdateRequired?: boolean;
};

export const ArticleTypeField: React.FC<IProps> = ({
  formFieldDefinition,
  value,
  previousValue,
  availableActions,
  onChange,
  entryPoint,
  isUpdateRequired
}) => {
  const { checkFormFieldValueIsUpdated } = useSubmissionFields(entryPoint);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [showDiffModal, setShowDiffModal] = useState<boolean>(false);
  const openDiffModal = () => setShowDiffModal(true);
  const closeDiffModal = () => setShowDiffModal(false);
  const { submissionId } = useSubmissionData();

  const changeArticleTypeEnabled = useFlag(FEATURE_FLAGS.changeArticleType);

  const isEditable = changeArticleTypeEnabled && !!onChange;

  const editCallback = isEditable
    ? () => {
        setIsEditing(true);
      }
    : undefined;

  const isUpdated = checkFormFieldValueIsUpdated(formFieldDefinition.name);

  return (
    <Flex flexDirection={'column'} gap={2}>
      {isEditing && onChange && (
        <ArticleTypeUpdateModal
          formField={formFieldDefinition}
          isOpen={isEditing}
          onCancel={() => {
            setIsEditing(false);
          }}
          value={value}
          onSubmit={(newValue: IChangeArticleType) => {
            setIsEditing(false);
            newValue.newName !== value && onChange(newValue);
          }}
          submissionId={submissionId}
        />
      )}

      {isUpdated && showDiffModal ? (
        <TextDiffModal
          config={formFieldDefinition}
          value={value}
          previousValue={previousValue}
          onClose={closeDiffModal}
        />
      ) : null}

      <FieldHeader
        {...formFieldDefinition}
        label={formFieldDefinition.params.labels.main}
        isUpdated={isUpdated}
        editCallback={editCallback}
        onShowDiffModal={openDiffModal}
        availableActions={availableActions}
        entryPoint={entryPoint}
        isUpdateRequired={isUpdateRequired}
      />

      {value ? <TextDisplay value={value} /> : <SelectEmptyState />}
    </Flex>
  );
};
